import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  bearbeiteSerie,
  loeseDauerauftragAuf,
  selectBuchungen,
} from 'modules/finanzen/features/finanzenSlice';
import { buchungenDerSerie } from 'modules/finanzen/features/berechnung';
import { ZAHLWEGE } from 'modules/finanzen/features/konstanten';
import { formatKurs } from 'core/utils/format';
import KategorieAuswahl from 'modules/finanzen/components/KategorieAuswahl';
import OrtAuswahl from 'modules/finanzen/components/OrtAuswahl';

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none';

/** Ein auswaehlbarer Zahlweg-Chip. */
function Chip({ aktiv, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
        aktiv ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-paper text-ink hover:border-brand-400'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Bearbeitet eine erkannte Serie (Dauerauftrag) gemeinsam: geaenderte Felder
 * (Kategorie, Notiz/Name, Zahlweg, Ort) werden auf den Dauerauftrag UND alle
 * verknuepften Buchungen angewendet. Serien erzeugen keine Buchungen mehr.
 */
export default function SerieBearbeitenModal({ dauerauftrag, onClose }) {
  const dispatch = useDispatch();
  const buchungen = useSelector(selectBuchungen);
  const anzahl = buchungenDerSerie(buchungen, dauerauftrag.uuid).length;

  const [kategorie, setKategorie] = useState(dauerauftrag.kategorie || '');
  const [notiz, setNotiz] = useState(dauerauftrag.notiz || '');
  const [zahlweg, setZahlweg] = useState(dauerauftrag.zahlweg || '');
  const [ort, setOrt] = useState(dauerauftrag.ort || '');
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  // Nur tatsaechlich geaenderte Felder werden auf die ganze Serie angewendet.
  const sammleAenderungen = () => {
    const aenderungen = {};
    if (kategorie !== dauerauftrag.kategorie) aenderungen.kategorie = kategorie;
    if (notiz.trim() !== (dauerauftrag.notiz || '')) aenderungen.notiz = notiz.trim();
    if (zahlweg !== dauerauftrag.zahlweg) aenderungen.zahlweg = zahlweg;
    if ((ort || null) !== (dauerauftrag.ort || null)) aenderungen.ort = ort || null;
    return aenderungen;
  };

  const speichern = async (e) => {
    e.preventDefault();
    const aenderungen = sammleAenderungen();
    if (Object.keys(aenderungen).length === 0) {
      onClose();
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(bearbeiteSerie({ uuid: dauerauftrag.uuid, aenderungen })).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
      setLaeuft(false);
    }
  };

  const aufloesen = async () => {
    if (!window.confirm(`Dauerauftrag auflösen? Die ${anzahl} Buchung(en) bleiben erhalten, verlieren aber die Verknüpfung.`)) {
      return;
    }
    setLaeuft(true);
    try {
      await dispatch(loeseDauerauftragAuf(dauerauftrag.uuid)).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Auflösen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="font-serif text-xl font-semibold text-ink">Dauerauftrag bearbeiten</h2>
        <p className="mb-4 mt-1 text-sm text-ink-muted">
          {anzahl} verknüpfte Buchung(en) · Ø {formatKurs(dauerauftrag.betrag)} · Änderungen gelten für die
          ganze Serie.
        </p>

        <form onSubmit={speichern} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Name / Notiz</label>
            <input value={notiz} onChange={(e) => setNotiz(e.target.value)} className={inputKlasse} placeholder="z. B. Netflix" />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Kategorie</label>
            <KategorieAuswahl typ={dauerauftrag.typ} wert={kategorie} onChange={setKategorie} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Zahlweg</label>
            <div className="flex flex-wrap gap-2">
              {ZAHLWEGE.map((z) => (
                <Chip key={z.name} aktiv={zahlweg === z.name} onClick={() => setZahlweg(z.name)}>
                  {z.icon} {z.name}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Investitionsort</label>
            <OrtAuswahl wert={ort} onChange={setOrt} />
          </div>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={aufloesen}
              disabled={laeuft}
              className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              Auflösen
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
              >
                {laeuft ? 'Speichere…' : 'Für ganze Serie speichern'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
